/**
 * Shared team missions board — one Firestore document all admins read/write.
 * Path: settings/team_missions_v1 (same rules as other settings/* for admins).
 */

import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import type { MissionRow, MissionsCatalog } from '../components/missionTypes.ts';
import {
  loadCatalog,
  loadRows,
  normalizeCatalogFromUnknown,
  normalizeMissionRowsFromUnknown,
} from '../components/missionStorage.ts';

const TEAM_MISSIONS_DOC = doc(db, 'settings', 'team_missions_v1');

export type TeamMissionsPayload = { catalog: MissionsCatalog; rows: MissionRow[] };

/**
 * Firestore forbids `undefined` in document fields (optional TS props become undefined).
 * Recursively omit undefined keys so setDoc never receives them.
 */
function stripUndefinedForFirestore<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedForFirestore(item)) as T;
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (v === undefined) continue;
    out[key] = typeof v === 'object' && v !== null ? stripUndefinedForFirestore(v) : v;
  }
  return out as T;
}

function teamDocPayload(catalog: MissionsCatalog, rows: MissionRow[]) {
  return {
    catalog: stripUndefinedForFirestore(catalog),
    rows: stripUndefinedForFirestore(rows),
    updatedAt: serverTimestamp(),
    schemaVersion: 1,
  };
}

/** Create the shared doc once, seeded from this browser’s local cache if empty. */
export async function ensureTeamMissionsDocumentSeed(): Promise<void> {
  const snap = await getDoc(TEAM_MISSIONS_DOC);
  if (snap.exists()) return;
  const catalog = loadCatalog();
  const rows = loadRows(catalog);
  await setDoc(TEAM_MISSIONS_DOC, teamDocPayload(catalog, rows));
}

export function subscribeTeamMissions(
  onData: (payload: TeamMissionsPayload) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    TEAM_MISSIONS_DOC,
    (snap) => {
      if (!snap.exists()) {
        onError?.(new Error('Team missions document not found'));
        return;
      }
      const d = snap.data() as { catalog?: unknown; rows?: unknown };
      const catalog = normalizeCatalogFromUnknown(d.catalog);
      const rows = normalizeMissionRowsFromUnknown(d.rows, catalog);
      onData({ catalog, rows });
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err)))
  );
}

export async function writeTeamMissions(catalog: MissionsCatalog, rows: MissionRow[]): Promise<void> {
  await setDoc(TEAM_MISSIONS_DOC, teamDocPayload(catalog, rows));
}
